<template>
    <!-- Multi-site point picker: Site > Area > Zone > Point. The special case;
         use PointSelectTree for one site. The caller passes the sites, as
         [{ id, name }], from its own server-side list; the picker never looks
         them up. Each site loads on its own and shows a loading or error row
         until it arrives; a site the user cannot access shows as an error row.
         Every site is requested through the page's own site, so loading the
         picker never changes the session's current site. Any other attribute
         falls through to TreeSelect. -->
    <tree-select    :model-value="selectedKeys"
                    :nodes="nodes"
                    search-placeholder="Search sites, areas, zones, or points"
                    :item-noun="{one: 'point', many: 'points'}"
                    empty-title="No points available"
                    empty-text="No points are available on these sites."
                    no-match-title="No matching points"
                    @update:model-value="emitSelection"
                    @retry="retry"
    ></tree-select>
</template>

<script>
import {areasToNodes, usePointTree} from "@/composables/usePointTree"

// Meter ids repeat across company databases, so leaves are keyed by site and
// meter together inside the component. Callers only see { siteId: [ids] }.
const siteKey = (siteId) => `s:${siteId}`
const leafKey = (siteId, meterId) => `${siteId}:${meterId}`

export default {
    name: "MultiSitePointSelectTree",
    emits: ['update:modelValue', 'loaded'],
    props: {
        // { siteId: [meterId, ...] }
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        // [{ id, name }]: the sites to show, in order.
        sites: {
            type: Array,
            default: () => ([]),
        },
        // Escape hatch for the playground: [{ id, name, areas?, status? }]. When set, nothing is loaded.
        tree: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            areasBySite: {},
            statusBySite: {},
            // Ignores late responses after `sites` changed mid-load.
            generation: 0,
        }
    },
    computed: {
        // An inline `:sites="['a', 'b']"` is a new array on every parent render;
        // watching this string instead of the array stops a reload loop.
        sitesKey() {
            return this.sites.map((site) => site.id).join(',')
        },
        sitesForTree() {
            if (this.tree !== null) {
                return this.tree
            }

            return this.sites.map((site) => ({
                ...site,
                areas: this.areasBySite[site.id],
                status: this.statusBySite[site.id] ?? null,
            }))
        },
        nodes() {
            return Object.freeze(this.sitesForTree.map((site) => {
                const status = site.status ?? (site.areas ? null : 'loading')
                if (status !== null) {
                    return {key: siteKey(site.id), label: site.name, status}
                }

                return {
                    key: siteKey(site.id),
                    label: site.name,
                    children: areasToNodes(site.areas, `${site.id}|`, (meter) => leafKey(site.id, meter.id)),
                }
            }))
        },
        selectedKeys() {
            return Object.entries(this.modelValue ?? {})
                .flatMap(([siteId, meterIds]) => meterIds.map((meterId) => leafKey(siteId, meterId)))
        },
    },
    watch: {
        sitesKey() {
            this.load()
        },
    },
    created() {
        this.load()
    },
    methods: {
        // Starts a fresh load of every site. Each site is marked loading, then
        // filled in by storeSite as its own request returns, at most four at a
        // time. Bumping `generation` makes any request still in flight from an
        // earlier `sites` list drop its result instead of writing into this one.
        load() {
            if (this.tree !== null) {
                return
            }

            const generation = ++this.generation
            this.areasBySite = {}
            this.statusBySite = Object.fromEntries(this.sites.map((site) => [site.id, 'loading']))

            usePointTree()
                .loadSites(
                    this.sites.map((site) => site.id),
                    (siteId, areas, error) => this.storeSite(generation, siteId, areas, error)
                )
                .then(() => {
                    if (generation === this.generation) {
                        this.$emit('loaded', this.areasBySite)
                    }
                })
        },
        // Records one site's result: its frozen area tree, or an error status
        // that TreeSelect renders as a retry row. The objects are replaced, not
        // mutated, so the `nodes` computed sees the change and rebuilds.
        storeSite(generation, siteId, areas, error) {
            if (generation !== this.generation) {
                return
            }

            if (error !== null) {
                this.statusBySite = {...this.statusBySite, [siteId]: 'error'}
                return
            }

            this.areasBySite = {...this.areasBySite, [siteId]: Object.freeze(areas)}
            this.statusBySite = {...this.statusBySite, [siteId]: null}
        },
        // TreeSelect's retry row hands back the site's node key (`s:{siteId}`).
        // Reloads that one site only, under the current generation, so a retry
        // that returns after `sites` changes is ignored like any other late result.
        retry(key) {
            const siteId = key.slice(2)
            const generation = this.generation
            this.statusBySite = {...this.statusBySite, [siteId]: 'loading'}

            usePointTree().fetchSiteTree(siteId)
                .then((areas) => this.storeSite(generation, siteId, areas, null))
                .catch((error) => this.storeSite(generation, siteId, null, error))
        },
        // Leaf keys back to { siteId: [meterIds] }. Sites with nothing selected are left out.
        // A leaf key is `{siteId}:{meterId}`; the split is on the last colon so a
        // site id is never cut short, and the meter id goes back to a number.
        emitSelection(keys) {
            const grouped = {}
            keys.forEach((key) => {
                const split = key.lastIndexOf(':')
                const siteId = key.slice(0, split)
                if (grouped[siteId] === undefined) {
                    grouped[siteId] = []
                }
                grouped[siteId].push(Number(key.slice(split + 1)))
            })

            this.$emit('update:modelValue', grouped)
        },
    },
}
</script>
